import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import "../css/CategorySection.css";
import "../css/Modal.css";

import Navbar from "../landingComponents/Navbar";
import Footer from "../landingComponents/Footer";
import AOS from "aos";
import "aos/dist/aos.css";

import {
  faArrowLeft,
  faCheck,
  faClock,
  faCertificate,
  faChartLine,
  faBookOpen,
  faUserTie,
  faTruck,
  faTemperatureLow,
  faMicrochip,
  faWarehouse,
  faSeedling,
  faPlaneDeparture,
  faBoxes,
  faShieldAlt,
  faTruckFast,
  faTruckLoading,
  faBoxOpen,
  faRoute,
  faHandshake,
  faShieldHalved,
  faDiagramProject,
  faGlobe,
  faShip,
  faBuildingCircleCheck,
  faAnchor,
} from "@fortawesome/free-solid-svg-icons";
import { accessCourse, BaseImgUrl, BaseUrl } from "../Services/Api";
import { Italic } from "lucide-react";

const iconMap = {
  faTruck,
  faWarehouse,
  faSeedling,
  faMicrochip,
  faPlaneDeparture,
  faBoxes,
  faShieldAlt,
  faTruckLoading,
  faTruckFast,
  faBoxOpen,
  faRoute,
  faHandshake,
  faShieldHalved,
  faDiagramProject,
  faGlobe,
  faShip,
  faBuildingCircleCheck,
  faAnchor,
};

const CategorySection = () => {
  const [categoryData, setCategoryData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchaseID, setPurchaseID] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [enrolledCourse, setEnrolledCourse] = useState(null);
  const [courseCart, setCourseCart] = useState(null);
  const [redirecting, setRedirecting] = useState(false);

  const { id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
        document.title = 'Category';

    AOS.init({ duration: 1000, once: true });
  }, []);

  useEffect(() => {
    setLoading(true);
    axios
      .get(`${BaseUrl}/category/${id}`, { withCredentials: true })
      .then((res) => {
        setCategoryData(res.data.data || null);
        if (res.data?.data) {
          setEnrolledCourse(res.data.data.enrolled_course);
          setCourseCart(res.data.data.course_cart);
        }
      })
      .catch((err) => {
        console.error(err);
        setCategoryData(null);
      })
      .finally(() => setLoading(false));
  }, [id, purchaseID]);

  const handleAddToCart = async () => {
    try {
      const data = { category_id: categoryData.category.id };
      const response = await fetch(`${BaseUrl}/addCart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        credentials: "include",
        body: JSON.stringify(data),
      });

      if (response.status === 401) {
        navigate("/login");
        return;
      }

      const result = await response.json();
      setPurchaseID(result.data.category_id);

      if (result.status === "success" || result.status === "exists") {
        setShowModal(true);
      } else {
        throw new Error(result.message || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);
    }
  };

  const handleAccessCourse = async () => {
    try {
      setRedirecting(true);
      const data = await accessCourse(id);
      // console.log(data);
      if (data.status === "success") {
        const form = document.createElement("form");
        form.method = "POST";
        form.action = data.redirect_url;

        ["encrypname", "token", "course_id"].forEach((field) => {
          const input = document.createElement("input");
          input.type = "hidden";
          input.name = field;
          input.value = data[field];
          form.appendChild(input);
        });

        document.body.appendChild(form);
        setTimeout(() => form.submit(), 800);
      } else {
        setRedirecting(false);
        alert("Access denied: " + data.message);
      }
    } catch (err) {
      setRedirecting(false);
      console.error("Error accessing course:", err);
      alert("Something went wrong while accessing the course.");
    }
  };

  const features = [
    {
      title: "180 hours on-demand content",
      description: "Learn at your own pace with comprehensive course materials",
      icon: faClock,
      color: "#FF5722",
    },
    {
      title: "Self Assessment Included",
      description: "Test your knowledge with built-in quizzes and assessments",
      icon: faChartLine,
      color: "#3F51B5",
    },
    {
      title: "Certificate of Completion",
      description:
        "Earn a recognized certificate upon successfully completing the program",
      icon: faCertificate,
      color: "#009688",
    },
  ];

  const renderLoadingSkeleton = () => (
    <div className="loading-skeleton">
      <div className="hero-section skeleton-bg">
        <div className="hero-content">
          <div className="skeleton-title"></div>
          <div className="skeleton-text short"></div>
          <div className="skeleton-text"></div>
          <div className="skeleton-button"></div>
        </div>
        <div className="hero-image skeleton-box"></div>
      </div>
      <div className="modules-section">
        <div className="section-header">
          <div className="skeleton-title short"></div>
          <div className="skeleton-text"></div>
        </div>
        <div className="modules-grid">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="module-card skeleton-box"></div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <>
      <Navbar />

      {loading && renderLoadingSkeleton()}

      {!loading && categoryData && (
        <div className="category-container">
          {/* === Main Course Content === */}
          <section className="agri-hero-section">
            <div className="agri-hero-card">
              <div className="agri-hero-image mobile-first">
                <div className="agri-image-box">
                  <img
                    src={`${BaseImgUrl}/${categoryData.category.new_category_img_url}`}
                    alt={categoryData.category.category_name}
                  />
                </div>
              </div>

              <div className="agri-hero-content">
                <h1>
                  {categoryData.category.category_full_name
                    .toLowerCase()
                    .replace(/\b\w/g, (char) => char.toUpperCase())}
                </h1>
                <p>{categoryData.category.category_desc}</p>

                <div className="agri-price-action-container">
                  {enrolledCourse == null ? (
                    courseCart == null ? (
                      <button
                        className="agri-get-started-btn"
                        onClick={handleAddToCart}
                      >
                        Add to cart
                      </button>

                      // <div style={{ fontStyle: 'italic' }}>
                      //   For fee details, please contact us at{' '}
                      //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                      //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                      // </div>


                    ) : courseCart.some((c) => c.hashed_id === id) ? (
                      <button className="agri-get-started-btn">
                        Added Cart
                      </button>

                      // <div style={{ fontStyle: 'italic' }}>
                      //   For fee details, please contact us at{' '}
                      //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                      //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                      // </div>

                    ) : (
                      <button
                        className="agri-get-started-btn"
                        onClick={handleAddToCart}
                      >
                        Add to cart
                      </button>

                      // <div style={{ fontStyle: 'italic' }}>
                      //   For fee details, please contact us at{' '}
                      //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                      //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                      // </div>

                    )
                  ) : enrolledCourse.some((c) => c.hashed_id === id) ? (
                    <button
                      className="agri-get-started-btn"
                      onClick={handleAccessCourse}
                    >
                      Get Access
                    </button>
                  ) : courseCart?.some((c) => c.hashed_id === id) ? (
                    <button className="agri-get-started-btn">Added Cart</button>
                  ) : (
                    <button
                      className="agri-get-started-btn"
                      onClick={handleAddToCart}
                    >
                      Add to cart
                    </button>

                    // <div style={{ fontStyle: 'italic' }}>
                    //   For fee details, please contact us at{' '}
                    //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                    //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                    // </div>

                  )}

                  {categoryData.category.gst_flag === 1 ? (
                    <h4 className="agri-course-price">
                      ₹{categoryData.category.actual_price}{" "}
                      <span
                        style={{
                          fontSize: "13px",
                          fontStyle: "italic",
                          fontWeight: "lighter",
                        }}
                      >
                        (Excl.GST)
                      </span>
                    </h4>
                  ) : (
                    <h4 className="agri-course-price">
                      ₹{categoryData.category.actual_price}
                    </h4>
                  )} 
                  {/* ---------Price Details */}
                </div>
              </div>

              <div className="agri-hero-image desktop-only">
                <div className="agri-image-box">
                  <img
                    src={`${BaseImgUrl}/${categoryData.category.new_category_img_url}`}
                    alt={categoryData.category.category_name}
                  />
                </div>
              </div>
            </div>
          </section>

          {/* Modules Section */}
          <section className="modules-section">
            <div className="section-header" data-aos="fade-up">
              <h2>Programme Modules</h2>
              <p>
                Three comprehensive courses to master{" "}
                {categoryData.category.category_full_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())}
              </p>
            </div>
            <div className="modules-grid">
              {categoryData.syllabuses.map((syllabus, index) => (
                <div
                  className="module-card"
                  key={syllabus.id}
                  data-aos="fade-up"
                  data-aos-delay={index * 100}
                >
                  <div
                    className="module-icon"
                    style={{ backgroundColor: syllabus.color }}
                  >
                    <FontAwesomeIcon
                      icon={iconMap[syllabus.icon] || faBoxes}
                    />
                  </div>
                  <h3>{syllabus.syllabus_topic}</h3>
                  <p className="syllabus-desc">{syllabus.syllabus_desc}</p>
                </div>
              ))}
            </div>
          </section>

          {/* Features Section */}
          <section className="features-section">
            <div className="features-container">
              <div className="features-text" data-aos="fade-right">
                <div className="centered-content">
                  <h2>Why Choose This Programme?</h2>
                  <p>
                    Our {categoryData.category.category_full_name} Certificate
                    Programme is designed by industry experts to provide you with
                    practical, real-world skills that you can apply immediately.
                  </p>
                  <div className="programme-details">
                    <div className="detail-item">
                      <FontAwesomeIcon
                        icon={faUserTie}
                        className="detail-icon"
                      />
                      <div>
                        <h4>Industry-Relevant Curriculum</h4>
                        <p>Designed with input from logistics professionals</p>
                      </div>
                    </div>
                    <div className="detail-item">
                      <FontAwesomeIcon
                        icon={faClock}
                        className="detail-icon"
                      />
                      <div>
                        <h4>Flexible Learning</h4>
                        <p>Complete at your own pace within 6 months</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="features-cards" data-aos="fade-left">
                {features.map((feature, index) => (
                  <div
                    className="feature-card"
                    key={index}
                    style={{ borderLeft: `4px solid ${feature.color}` }}
                  >
                    <div className="card-content-wrapper">
                      <div
                        className="feature-icon"
                        style={{ color: feature.color }}
                      >
                        <FontAwesomeIcon icon={feature.icon} />
                      </div>
                      <div className="feature-text-content">
                        <h4>{feature.title}</h4>
                        <p>{feature.description}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="cta-section">
            <div className="cta-container" data-aos="zoom-in">
              <h2>
                Start Your{" "}
                {categoryData.category.category_full_name
                  .toLowerCase()
                  .replace(/\b\w/g, (char) => char.toUpperCase())}{" "}
                Journey Today
              </h2>
              <p>
                Join hundreds of professionals who have advanced their careers
                with our certificate programme
              </p>
              <div className="cta-buttons">
                {enrolledCourse == null ? (
                  courseCart == null ? (
                    <button className="primary-cta" onClick={handleAddToCart}>
                      Add to cart
                    </button>

                    // <div style={{ fontStyle: 'italic' }}>
                    //   For fee details, please contact us at{' '}
                    //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                    //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                    // </div>

                  ) : courseCart.some((c) => c.hashed_id === id) ? (
                    <button className="primary-cta">Added Cart</button>
                  ) : (
                    <button className="primary-cta" onClick={handleAddToCart}>
                      Add to cart
                    </button>

                    // <div style={{ fontStyle: 'italic' }}>
                    //   For fee details, please contact us at{' '}
                    //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                    //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                    // </div>

                  )
                ) : enrolledCourse.some((c) => c.hashed_id === id) ? (
                  <button className="primary-cta" onClick={handleAccessCourse}>
                    Get Access
                  </button>
                ) : courseCart?.some((c) => c.hashed_id === id) ? (
                  <button className="primary-cta">Added Cart</button>
                ) : (
                  <button className="primary-cta" onClick={handleAddToCart}>
                    Add to cart
                  </button>

                  // <div style={{ fontStyle: 'italic' }}>
                  //   For fee details, please contact us at{' '}
                  //   <a href="mailto:solutions@lsc-india.com">solutions@lsc-india.com</a> or{' '}
                  //   <a href="mailto:support.eid@lsc-india.com">support.eid@lsc-india.com</a>.
                  // </div>



                )}
              </div>
            </div>
          </section>
        </div>
      )}

      {!loading && !categoryData && (
        <div className="no-category-container">
          <div className="no-category-content">
            <div className="no-category-text">
              <h1 className="no-category-title">Category Not Found</h1>
              <div className="no-category-divider"></div>
              <p className="no-category-description">
                We couldn't find the category you're looking for. It might have
                been moved, renamed, or doesn't exist yet.
              </p>
              <p className="no-category-description">
                Don't worry though – we have plenty of other categories for you
                to explore!
              </p>
              <Link to="/" className="no-category-button">
                Explore Available Categories{" "}
                <i className="fas fa-arrow-right"></i>
              </Link>
            </div>
            <div className="no-category-image">
              <div className="no-category-image-placeholder">
                <i className="fas fa-search"></i>
                <p>Visual representation of exploring categories and content</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />

      {/* Modal */}
      {showModal && categoryData && (
        <div className="modal-overlay">
          <div className="modal-content">
            <button className="modal-close" onClick={() => setShowModal(false)}>
              &times;
            </button>
            <h2 className="modal-title">Added to Cart</h2>
            <p className="modal-course-name">
              {categoryData.category.category_full_name}
            </p>
            <p className="modal-price">Price: ₹1,500</p>
            <p className="modal-message">
              Your course has been added to the cart successfully 🎉
            </p>
          </div>
        </div>
      )}

      {/* Redirect Loader */}
      {redirecting && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            background: "rgba(255,255,255,0.9)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontSize: "20px",
            fontWeight: "bold",
            zIndex: 9999,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <div
              style={{
                width: "50px",
                height: "50px",
                border: "5px solid #ccc",
                borderTop: "5px solid #007bff",
                borderRadius: "50%",
                animation: "spin 1s linear infinite",
                margin: "0 auto 15px auto",
              }}
            ></div>
            Redirecting to course...
          </div>
          <style>
            {`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}
          </style>
        </div>
      )}
    </>
  );
};

export default CategorySection;
